import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Inject,
  Input, OnDestroy, OnInit, Output, ViewEncapsulation
} from '@angular/core';
import { isEmpty } from 'lodash-es';
import { BehaviorSubject, Observable, Subscription, zip, iif, of, combineLatest } from 'rxjs';
import { filter, map, take, switchMap, tap, distinctUntilChanged } from 'rxjs/operators';

import { isNullOrUndefined } from '@app/util/util';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { SelectAutocompletePrediction } from '@search-bar/entities/select-autocomplete-prediction';
import { SearchType } from '@search-bar/entities/search-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { EventProperties } from '@avo/interface/trigger-props';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-auto-search-places',
  templateUrl: './auto-search-places.component.html',
  styleUrls: ['./auto-search-places.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class AutoSearchPlacesComponent implements OnInit, OnDestroy {
  public pickUp: PlaceType = PlaceType.DEPARTURE;
  public dropOff: PlaceType = PlaceType.ARRIVAL;
  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);

  private subscriptions: Subscription[] = [];
  private placeDropOffSelectedItem: SelectAutocompletePrediction;
  private placePickUpSelectedItem: SelectAutocompletePrediction;

  @Input() searchType: SearchType = SearchType.HOME_AUTOS;
  @Input() loadSearchData: boolean = false;
  @Input() isMobile: boolean;
  @Input() isSubmitted: boolean = false;
  @Input() isResults: boolean = false;
  @Input() autoDetail: boolean = false;

  @Output() showDropOff: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showPickUp: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() placeDropOffSelected: EventEmitter<SelectAutocompletePlace> = new EventEmitter<SelectAutocompletePlace>();
  @Output() placePickUpSelected: EventEmitter<SelectAutocompletePlace> = new EventEmitter<SelectAutocompletePlace>();
  @Output() inputChanged: EventEmitter<ChangeInputPayload> = new EventEmitter<ChangeInputPayload>();

  @Output() locationNotFoundEvent: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();
  @Output() popularSelected: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();

  @HostListener('document:click', ['$event']) clickout(event: Event): void {
    if (this.isBrowser) this.eventPath.next(event.composedPath());
  }

  public readonly icons = Icon;

  constructor(
    private baseSearchbarfacade: BaseSearchBarFacade,
    private cdr: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (!this.isResults && !this.autoDetail) {
        this.baseSearchbarfacade.loadSearchData(SearchBy.AUTOS);
      }

      combineLatest([this.baseSearchbarfacade.searchData$, this.isDiffPlace$])
        .pipe(
          distinctUntilChanged(),
          filter(([searchData]) => {
            const { pickUpPlace, dropOffPlace } = searchData || {};
            const arePlacesEqual = pickUpPlace && dropOffPlace &&
            pickUpPlace.description === dropOffPlace.description &&
            pickUpPlace.code === dropOffPlace.code &&
            pickUpPlace.country === dropOffPlace.country &&
            pickUpPlace.is_airport === dropOffPlace.is_airport;
            return !isEmpty(searchData) && arePlacesEqual;
          }),
          take(1),
          tap(([, isDiffPlace]) => {
            if (isDiffPlace) {
              this.baseSearchbarfacade.toggleAutoDiffPlace();
              this.cdr.detectChanges(); this.cdr.markForCheck();
            }
          })
        )
        .subscribe();

      this.subscriptions.push(
        this.baseSearchbarfacade.searchData$.pipe(filter(data => !!data))
          .pipe(map(searchData => ({ searchData }))).subscribe(({ searchData }) => {
            this.placePickUpSelectedItem = searchData.pickUpPlace;
            this.placeDropOffSelectedItem = searchData.dropOffPlace;
            this.cdr.detectChanges(); this.cdr.markForCheck();
          }));
    }
  }

  ngOnDestroy(): void {
    this.cdr.detach();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isAutoDetail(): boolean {
    return this.searchType === SearchType.AUTOS_DETAIL; 
  }

  get searchButtonLabel(): string {
    return this.isAutoDetail ? 'BUTTONS.EDIT' : 'BUTTONS.SEARCH'; 
  }

  get autoSearchHome(): boolean {
    return this.searchType === SearchType.HOME_AUTOS; 
  }

  get autoCompletePlaces$(): Observable<AutocompletePlaces> {
    return this.baseSearchbarfacade.autoCompletePlaces$;
  }

  get pickUpPlace$(): Observable<AutocompletePlace> {
    return this.baseSearchbarfacade.pickUpPlace$;
  }

  get dropOffPlace$(): Observable<AutocompletePlace> {
    return this.baseSearchbarfacade.dropOffPlace$;
  }

  get isDiffPlace$(): Observable<boolean> {
    return this.baseSearchbarfacade.autoDiffPlace$;
  }

  get isLoadingAutocompletePlaces$(): Observable<boolean> {
    return this.baseSearchbarfacade.isLoadingAutocompletePlaces$;
  }

  get isPlacePickUpSelectedEmpty(): boolean {
    return (isNullOrUndefined(this.placePickUpSelectedItem) ||
      isEmpty(this.placePickUpSelectedItem) ||
      !(this.placePickUpSelectedItem.description)) && this.isSubmitted;
  }

  get isPlaceDropOffSelectedEmpty(): boolean {
    return (isNullOrUndefined(this.placeDropOffSelectedItem) ||
      isEmpty(this.placeDropOffSelectedItem) ||
      !(this.placeDropOffSelectedItem.description))
      && this.isSubmitted;
  }

  public addErrorClass(field: string): { [key: string]: boolean } {
    return {field: true};
  }

  public selectPlace(placeSelected: SelectAutocompletePrediction, type: PlaceType): void {

    const isArrival: boolean = type === PlaceType.ARRIVAL;

    const selectPlaceDropOff = (place: SelectAutocompletePrediction): void => {
      this.placeDropOffSelected.emit(place);
      this.placeDropOffSelectedItem = place;
    };

    const selectPlacePickUp = (place: SelectAutocompletePrediction): void => {
      this.placePickUpSelected.emit(place);
      this.placePickUpSelectedItem = place;
    };

    zip(this.isDiffPlace$, this.baseSearchbarfacade.searchData$)
      .pipe(
        take(1),
        switchMap(([isDiffPlace, searchData]) =>
          iif(() => isDiffPlace && isArrival,
            of({ place: { ...placeSelected } as SelectAutocompletePrediction, searchData, isDiffPlace }),
            of({
              place: {
                ...placeSelected,
                type: isDiffPlace ? PlaceType.DEPARTURE : PlaceType.ARRIVAL
              } as SelectAutocompletePrediction,
              searchData, isDiffPlace
            })
          )),
        tap(({ place, searchData, isDiffPlace }) => {
          this.baseSearchbarfacade.selectAutoPlaces(place);
          if (isDiffPlace) {
            isArrival ? selectPlaceDropOff(place) : selectPlacePickUp(place);
          } else {
            selectPlacePickUp(place);
            selectPlaceDropOff({ ...place, type: PlaceType.DEPARTURE });
          }
        })
      ).subscribe().unsubscribe();
  }

  public clearAutocompletePlaces(): void {
    this.baseSearchbarfacade.resetAutocomplete();
  }

  public toggleOrigin(): void {
    this.baseSearchbarfacade.toggleAutoDiffPlace();
    this.clearDropOffPlace();
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  private clearDropOffPlace(): void {
    zip(this.baseSearchbarfacade.searchData$, this.isDiffPlace$)
      .pipe(
        take(1),
        switchMap(([searchData, isDiffPlace]) =>
          iif(() => isDiffPlace,
            of({ place: {} as SelectAutocompletePrediction, searchData }),
            of({ place: { ...searchData.pickUpPlace, type: PlaceType.ARRIVAL } as SelectAutocompletePrediction, searchData })
          )),
        tap(({ place, searchData }) => {
          this.baseSearchbarfacade.selectAutoDropOffPlace(place);
        })
      ).subscribe().unsubscribe();
  }

  public placeInputChanged(input: ChangeInputPayload): void {
    if (input?.value?.length > 0) {
      this.inputChanged.emit(input);
      this.cdr.detectChanges();
    }
  }
}
